function cellKey(employeeId, weekKey) {
    return `${Number(employeeId)}|${String(weekKey || '')}`;
}

function parseCellKey(value) {
    const source = String(value || '');
    const separator = source.indexOf('|');
    if (separator < 1) return null;
    const employeeId = Number(source.slice(0, separator));
    const weekKey = source.slice(separator + 1);
    if (!Number.isFinite(employeeId) || !weekKey) return null;
    return {employee_id: employeeId, week_key: weekKey};
}

function uniqueKeys(keys) {
    return Array.from(new Set(Array.isArray(keys) ? keys : []));
}

function toggleCell(keys, key) {
    const next = new Set(uniqueKeys(keys));
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return Array.from(next);
}

function addCells(keys, additions) {
    return Array.from(new Set(uniqueKeys(keys).concat(uniqueKeys(additions))));
}

function rectangleCellKeys(options) {
    const opts = options || {};
    const employees = Array.isArray(opts.employees) ? opts.employees : [];
    const weeks = Array.isArray(opts.weeks) ? opts.weeks : [];
    const start = opts.start || {};
    const end = opts.end || {};
    const employeeIndexes = opts.employeeIndexes instanceof Map
        ? opts.employeeIndexes
        : new Map(employees.map((employee, index) => (
            [Number(employee.employee_id), index]
        )));
    const weekIndexes = opts.weekIndexes instanceof Map
        ? opts.weekIndexes
        : new Map(weeks.map((week, index) => [week.key, index]));
    const startRow = employeeIndexes.has(Number(start.employee_id))
        ? employeeIndexes.get(Number(start.employee_id))
        : -1;
    const endRow = employeeIndexes.has(Number(end.employee_id))
        ? employeeIndexes.get(Number(end.employee_id))
        : -1;
    const startColumn = weekIndexes.has(start.week_key)
        ? weekIndexes.get(start.week_key)
        : -1;
    const endColumn = weekIndexes.has(end.week_key)
        ? weekIndexes.get(end.week_key)
        : -1;
    if ([startRow, endRow, startColumn, endColumn].some(index => index < 0)) {
        return {keys: [], blocked_employee: null};
    }
    const rowStep = endRow >= startRow ? 1 : -1;
    const allowedRows = [];
    let blockedEmployee = null;
    for (
        let rowIndex = startRow;
        rowStep > 0 ? rowIndex <= endRow : rowIndex >= endRow;
        rowIndex += rowStep
    ) {
        const employee = employees[rowIndex];
        if (opts.canEditEmployee && !opts.canEditEmployee(employee)) {
            blockedEmployee = employee;
            break;
        }
        allowedRows.push(rowIndex);
    }
    const columnFrom = Math.min(startColumn, endColumn);
    const columnTo = Math.max(startColumn, endColumn);
    const keys = [];
    allowedRows.forEach(function (rowIndex) {
        for (let column = columnFrom; column <= columnTo; column += 1) {
            keys.push(cellKey(employees[rowIndex].employee_id, weeks[column].key));
        }
    });
    return {keys, blocked_employee: blockedEmployee};
}

function selectionRanges(keys, employees, weeks, window) {
    const selectedByEmployee = new Map();
    uniqueKeys(keys).forEach(function (key) {
        const coordinate = parseCellKey(key);
        if (!coordinate) return;
        if (!selectedByEmployee.has(coordinate.employee_id)) {
            selectedByEmployee.set(coordinate.employee_id, []);
        }
        selectedByEmployee.get(coordinate.employee_id).push(coordinate.week_key);
    });
    const weekIndexes = new Map(
        (Array.isArray(weeks) ? weeks : []).map(function (week, index) {
            return [week.key, index];
        })
    );
    const ranges = [];
    (Array.isArray(employees) ? employees : []).forEach(function (employee) {
        const indexes = (selectedByEmployee.get(Number(employee.employee_id)) || [])
            .map(weekKey => weekIndexes.get(weekKey))
            .filter(index => Number.isInteger(index))
            .sort((left, right) => left - right)
            .filter((index, position, values) => !position || index !== values[position - 1]);
        if (!indexes.length) return;
        let first = indexes[0];
        let previous = first;
        const pushRange = function (last) {
            const firstWeek = weeks[first];
            const lastWeek = weeks[last];
            ranges.push({
                employee_id: Number(employee.employee_id),
                starts_on: firstWeek.starts_on < window.from
                    ? window.from
                    : firstWeek.starts_on,
                ends_on: lastWeek.ends_on > window.to
                    ? window.to
                    : lastWeek.ends_on
            });
        };
        indexes.slice(1).forEach(function (index) {
            if (index === previous + 1) {
                previous = index;
                return;
            }
            pushRange(previous);
            first = index;
            previous = index;
        });
        pushRange(previous);
    });
    return ranges;
}

function unifiedSelectionPeriod(ranges) {
    const values = Array.isArray(ranges) ? ranges : [];
    if (!values.length) return null;
    const employees = new Set(values.map(range => Number(range.employee_id)));
    if (values.length !== employees.size) return null;
    const first = values[0];
    return values.every(range => (
        range.starts_on === first.starts_on && range.ends_on === first.ends_on
    )) ? {starts_on: first.starts_on, ends_on: first.ends_on} : null;
}

module.exports = {
    addCells,
    cellKey,
    parseCellKey,
    rectangleCellKeys,
    selectionRanges,
    toggleCell,
    unifiedSelectionPeriod,
    uniqueKeys
};
